import React, { useEffect, useState } from 'react';
import {
    Bot,
    Loader2,
    MessageCircle,
    ThumbsUp,
    ThumbsDown,
    Star,
    MapPin,
    Briefcase,
    Building2,
    GraduationCap,
    User,
    X,
    Check,
    ChevronRight,
    Sparkles,
} from 'lucide-react';
import CompanyLayout from '../components/CompanyLayout';
import {
    listMyDavidConversations,
    getMyDavidMessages,
    sendFeedback,
    sendQuickResponse,
    DavidCompanyConversation,
    DavidCompanyMessage,
    CandidateRecommendation,
} from '../services/companyDavidInboxApi';

// ────────────────────────────────────────────────────────────────────────────
// CompanyDavidInbox — 3-pane David inbox for company owners / recruiters.
// ────────────────────────────────────────────────────────────────────────────

const QUICK_REPLIES = [
    { text: "Yes, let's chat!", emoji: "👋" },
    { text: "Tell me more", emoji: "💬" },
    { text: "Send more like this", emoji: "🔄" },
    { text: "Not right now", emoji: "⏸️" },
];

const CompanyDavidInbox: React.FC = () => {
    const [conversations, setConversations] = useState<DavidCompanyConversation[]>([]);
    const [conversationsLoading, setConversationsLoading] = useState(true);
    const [activeConvId, setActiveConvId] = useState<string | null>(null);
    const [messages, setMessages] = useState<DavidCompanyMessage[]>([]);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [selectedCandidate, setSelectedCandidate] = useState<CandidateRecommendation | null>(null);

    useEffect(() => {
        setConversationsLoading(true);
        listMyDavidConversations()
            .then(setConversations)
            .catch((err) => console.error('Failed to load David conversations:', err))
            .finally(() => setConversationsLoading(false));
    }, []);

    useEffect(() => {
        if (!activeConvId) { setMessages([]); return; }
        setMessagesLoading(true);
        getMyDavidMessages(activeConvId)
            .then(setMessages)
            .catch((err) => console.error('Failed to load messages:', err))
            .finally(() => setMessagesLoading(false));
    }, [activeConvId]);

    const handleFeedback = async (msgId: string, type: 'thumbs_up' | 'thumbs_down') => {
        setMessages((prev) =>
            prev.map((m) => m.id === msgId ? { ...m, feedback: { type, created_at: new Date().toISOString() } } : m)
        );
        try { await sendFeedback(msgId, type); } catch (err) { console.error('Feedback failed:', err); }
    };

    const handleQuickResponse = async (msgId: string, text: string) => {
        setMessages((prev) =>
            prev.map((m) => m.id === msgId ? { ...m, quick_response: { text, created_at: new Date().toISOString() } } : m)
        );
        try { await sendQuickResponse(msgId, text); } catch (err) { console.error('Quick response failed:', err); }
    };

    return (
        <CompanyLayout>
            <div className="h-[calc(100vh-140px)] flex flex-col rounded-2xl overflow-hidden shadow-sm" style={{ background: 'linear-gradient(135deg, #fafbff 0%, #f5f3ff 50%, #faf5ff 100%)' }}>
                {/* Header */}
                <div className="px-6 py-4 flex items-center gap-4 flex-shrink-0 bg-white/70 backdrop-blur-sm border-b border-gray-100/60">
                    <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-500 via-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-200/50">
                        <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900 tracking-tight">David</h1>
                        <p className="text-xs text-gray-400 font-medium">Candidate suggestions from the Hush team</p>
                    </div>
                </div>

                {/* 3-pane body */}
                <div className="flex-1 flex overflow-hidden min-h-0">
                    {/* LEFT — Conversations */}
                    <div className="w-[300px] bg-white/50 backdrop-blur-sm flex flex-col overflow-hidden border-r border-gray-100/50">
                        <div className="px-5 py-3.5 flex-shrink-0">
                            <h2 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Messages</h2>
                        </div>
                        <div className="flex-1 overflow-y-auto px-2">
                            {conversationsLoading && (
                                <div className="p-8 text-center">
                                    <Loader2 className="w-5 h-5 animate-spin text-indigo-400 mx-auto" />
                                </div>
                            )}
                            {!conversationsLoading && conversations.length === 0 && (
                                <div className="p-8 text-center">
                                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center mx-auto mb-4">
                                        <MessageCircle className="w-7 h-7 text-indigo-300" />
                                    </div>
                                    <p className="text-sm font-medium text-gray-500">No messages yet</p>
                                    <p className="text-xs text-gray-400 mt-1 leading-relaxed">When the Hush team finds candidates for your roles, they'll show up here</p>
                                </div>
                            )}
                            {conversations.map((conv) => (
                                <button
                                    key={conv.id}
                                    onClick={() => { setActiveConvId(conv.id); setSelectedCandidate(null); }}
                                    className={`w-full text-left px-3.5 py-3 mb-1 rounded-xl transition-all duration-200 ${
                                        activeConvId === conv.id
                                            ? 'bg-white shadow-sm shadow-indigo-100/80 ring-1 ring-indigo-100/80'
                                            : 'hover:bg-white/80'
                                    }`}
                                >
                                    <div className="flex items-center gap-2.5 mb-1">
                                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                            activeConvId === conv.id
                                                ? 'bg-gradient-to-br from-violet-500 to-indigo-600'
                                                : 'bg-gradient-to-br from-gray-200 to-gray-300'
                                        }`}>
                                            <Bot className="w-3.5 h-3.5 text-white" />
                                        </div>
                                        <span className={`text-sm font-semibold ${activeConvId === conv.id ? 'text-gray-900' : 'text-gray-600'}`}>
                                            David
                                        </span>
                                        <span className="text-[10px] text-gray-400 ml-auto font-medium">
                                            {new Date(conv.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        </span>
                                    </div>
                                    {conv.last_message && (
                                        <p className="text-xs text-gray-400 line-clamp-2 ml-[38px] leading-relaxed">
                                            {conv.last_message.content}
                                        </p>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* MIDDLE — Messages */}
                    <div className="flex-1 overflow-y-auto">
                        {!activeConvId && (
                            <div className="h-full flex items-center justify-center">
                                <div className="text-center px-8">
                                    <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center mx-auto mb-4">
                                        <Bot className="w-8 h-8 text-indigo-300" />
                                    </div>
                                    <p className="text-sm font-medium text-gray-500">Select a conversation</p>
                                    <p className="text-xs text-gray-400 mt-1">View candidate suggestions from David</p>
                                </div>
                            </div>
                        )}
                        {activeConvId && messagesLoading && (
                            <div className="h-full flex items-center justify-center">
                                <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
                            </div>
                        )}
                        {activeConvId && !messagesLoading && (
                            <div className="p-6 space-y-8 max-w-3xl">
                                {messages.map((msg) => (
                                    <MessageBlock
                                        key={msg.id}
                                        message={msg}
                                        onCandidateClick={setSelectedCandidate}
                                        onFeedback={handleFeedback}
                                        onQuickResponse={handleQuickResponse}
                                        selectedCandidateId={selectedCandidate?.candidate_id}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* RIGHT — Profile panel */}
                    <div className={`bg-white/80 backdrop-blur-sm flex flex-col overflow-hidden transition-all duration-300 ease-in-out ${
                        selectedCandidate ? 'w-[380px] border-l border-gray-100/50' : 'w-0'
                    }`}>
                        {selectedCandidate && (
                            <CandidateProfilePanel
                                candidate={selectedCandidate}
                                onClose={() => setSelectedCandidate(null)}
                            />
                        )}
                    </div>
                </div>
            </div>
        </CompanyLayout>
    );
};

// ────────────────────────────────────────────────────────────────────────────
// MessageBlock
// ────────────────────────────────────────────────────────────────────────────

interface MessageBlockProps {
    message: DavidCompanyMessage;
    onCandidateClick: (c: CandidateRecommendation) => void;
    onFeedback: (msgId: string, type: 'thumbs_up' | 'thumbs_down') => void;
    onQuickResponse: (msgId: string, text: string) => void;
    selectedCandidateId?: string;
}

const MessageBlock: React.FC<MessageBlockProps> = ({
    message, onCandidateClick, onFeedback, onQuickResponse, selectedCandidateId,
}) => {
    const hasCandidates = message.candidate_recommendations && message.candidate_recommendations.length > 0;

    return (
        <div className="space-y-4">
            {/* David header */}
            <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 via-indigo-500 to-purple-600 flex items-center justify-center shadow-md shadow-indigo-200/40 flex-shrink-0">
                    <Bot className="w-4.5 h-4.5 text-white" />
                </div>
                <div>
                    <span className="text-sm font-bold text-gray-900">David</span>
                    <span className="text-[11px] text-gray-400 ml-2 font-medium">
                        {new Date(message.created_at).toLocaleString('en-US', {
                            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                        })}
                    </span>
                </div>
            </div>

            {/* Message body */}
            <div className="ml-12 space-y-4">
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl rounded-tl-md px-5 py-4 shadow-sm shadow-gray-100/80 ring-1 ring-gray-100/50">
                    <p className="text-[13.5px] text-gray-700 whitespace-pre-wrap leading-relaxed">
                        {message.content}
                    </p>
                </div>

                {/* Candidate cards */}
                {hasCandidates && (
                    <div className="space-y-2.5">
                        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                            <User className="w-3 h-3" />
                            {message.candidate_recommendations!.length} Suggested Candidate{message.candidate_recommendations!.length > 1 ? 's' : ''}
                        </p>
                        {message.candidate_recommendations!.map((c, i) => (
                            <CandidateCard
                                key={c.candidate_id + i}
                                candidate={c}
                                rank={i + 1}
                                isSelected={selectedCandidateId === c.candidate_id}
                                onClick={() => onCandidateClick(c)}
                            />
                        ))}
                    </div>
                )}

                {/* Feedback + Quick Response */}
                <div className="space-y-3 pt-1">
                    {/* Thumbs */}
                    <div className="flex items-center gap-2">
                        <span className="text-[11px] text-gray-400 font-medium mr-0.5">Helpful?</span>
                        <button
                            onClick={() => onFeedback(message.id, 'thumbs_up')}
                            className={`p-1.5 rounded-lg transition-all duration-200 ${
                                message.feedback?.type === 'thumbs_up'
                                    ? 'bg-emerald-50 text-emerald-500 shadow-sm shadow-emerald-100/50 ring-1 ring-emerald-200/60'
                                    : 'text-gray-300 hover:text-emerald-400 hover:bg-emerald-50/50'
                            }`}
                        >
                            <ThumbsUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                            onClick={() => onFeedback(message.id, 'thumbs_down')}
                            className={`p-1.5 rounded-lg transition-all duration-200 ${
                                message.feedback?.type === 'thumbs_down'
                                    ? 'bg-rose-50 text-rose-500 shadow-sm shadow-rose-100/50 ring-1 ring-rose-200/60'
                                    : 'text-gray-300 hover:text-rose-400 hover:bg-rose-50/50'
                            }`}
                        >
                            <ThumbsDown className="w-3.5 h-3.5" />
                        </button>
                        {message.feedback && (
                            <span className="text-[10px] text-gray-400 ml-0.5 font-medium">
                                {message.feedback.type === 'thumbs_up' ? 'Thanks!' : 'Noted — we\'ll adjust'}
                            </span>
                        )}
                    </div>

                    {/* Reply chips */}
                    {!message.quick_response ? (
                        <div className="flex flex-wrap gap-2">
                            {QUICK_REPLIES.map((reply) => (
                                <button
                                    key={reply.text}
                                    onClick={() => onQuickResponse(message.id, reply.text)}
                                    className="px-3.5 py-1.5 text-xs font-medium rounded-full bg-white/80 text-indigo-600 ring-1 ring-indigo-100/80 hover:bg-indigo-50 hover:ring-indigo-200/80 hover:shadow-sm hover:shadow-indigo-100/40 transition-all duration-200"
                                >
                                    <span className="mr-1">{reply.emoji}</span>
                                    {reply.text}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 px-3.5 py-2 bg-emerald-50/50 rounded-xl ring-1 ring-emerald-100/50">
                            <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                            <span className="text-xs text-gray-500">You replied:</span>
                            <span className="text-xs font-semibold text-gray-700">"{message.quick_response.text}"</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ────────────────────────────────────────────────────────────────────────────
// CandidateCard
// ────────────────────────────────────────────────────────────────────────────

const SOURCE_LABELS: Record<string, { label: string; bg: string; text: string }> = {
    platform: { label: 'Hush', bg: 'bg-emerald-50', text: 'text-emerald-600' },
    imported: { label: 'Network', bg: 'bg-sky-50', text: 'text-sky-600' },
    imported_se: { label: 'Engineering', bg: 'bg-violet-50', text: 'text-violet-600' },
};

const CandidateCard: React.FC<{
    candidate: CandidateRecommendation;
    rank: number;
    isSelected: boolean;
    onClick: () => void;
}> = ({ candidate, rank, isSelected, onClick }) => {
    const qwen = typeof candidate.qwen_score === 'number' ? candidate.qwen_score : null;
    const sourceBadge = candidate.source ? SOURCE_LABELS[candidate.source] : null;

    return (
        <button
            onClick={onClick}
            className={`w-full text-left rounded-2xl p-4 transition-all duration-200 group ${
                isSelected
                    ? 'bg-white shadow-md shadow-indigo-100/60 ring-2 ring-indigo-400/30 scale-[1.01]'
                    : 'bg-white/70 hover:bg-white hover:shadow-sm hover:shadow-gray-100/80 ring-1 ring-gray-100/40 hover:ring-gray-200/60'
            }`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm font-bold flex-shrink-0 shadow-sm ${
                        isSelected
                            ? 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-indigo-200/50'
                            : 'bg-gradient-to-br from-gray-400 to-gray-500 group-hover:from-indigo-400 group-hover:to-purple-500'
                    } transition-all duration-200`}>
                        {rank}
                    </div>
                    <div className="min-w-0">
                        <h4 className={`text-sm font-semibold truncate transition-colors ${
                            isSelected ? 'text-indigo-700' : 'text-gray-800 group-hover:text-indigo-600'
                        }`}>
                            {candidate.name}
                        </h4>
                        {candidate.titles && candidate.titles[0] && (
                            <p className="text-[11px] text-gray-400 truncate font-medium">{candidate.titles[0]}</p>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {qwen !== null && (
                        <div className="flex items-center gap-0.5 px-2 py-0.5 rounded-lg bg-amber-50 text-amber-600">
                            <Star className="w-3 h-3" />
                            <span className="text-[11px] font-bold">{qwen.toFixed(0)}</span>
                        </div>
                    )}
                    {sourceBadge && (
                        <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold ${sourceBadge.bg} ${sourceBadge.text}`}>
                            {sourceBadge.label}
                        </span>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-3 mt-2.5 ml-12 text-[11px] text-gray-400 font-medium flex-wrap">
                {typeof candidate.years_experience === 'number' && candidate.years_experience > 0 && (
                    <span className="flex items-center gap-1">
                        <Briefcase className="w-3 h-3" />
                        {candidate.years_experience}y exp
                    </span>
                )}
                {candidate.locations && candidate.locations[0] && (
                    <span className="flex items-center gap-1 truncate">
                        <MapPin className="w-3 h-3" />
                        {candidate.locations[0]}
                    </span>
                )}
                {candidate.skills && candidate.skills.length > 0 && (
                    <span className="truncate">
                        {candidate.skills.slice(0, 3).join(' · ')}
                    </span>
                )}
            </div>

            <div className={`mt-2 ml-12 flex items-center gap-1 text-[10px] font-semibold transition-all duration-200 ${
                isSelected ? 'text-indigo-500 opacity-100' : 'text-indigo-400 opacity-0 group-hover:opacity-100'
            }`}>
                View profile <ChevronRight className="w-3 h-3" />
            </div>
        </button>
    );
};

// ────────────────────────────────────────────────────────────────────────────
// CandidateProfilePanel
// ────────────────────────────────────────────────────────────────────────────

const CandidateProfilePanel: React.FC<{
    candidate: CandidateRecommendation;
    onClose: () => void;
}> = ({ candidate, onClose }) => {
    const qwen = typeof candidate.qwen_score === 'number' ? candidate.qwen_score : null;
    const vec = typeof candidate.vector_similarity === 'number' ? candidate.vector_similarity : null;
    const qwenColor = qwen !== null
        ? qwen >= 80 ? 'from-emerald-400 to-emerald-500' : qwen >= 50 ? 'from-amber-400 to-amber-500' : 'from-gray-400 to-gray-500'
        : 'from-gray-300 to-gray-400';

    return (
        <div className="h-full flex flex-col">
            {/* Header */}
            <div className="px-5 py-4 flex items-center justify-between flex-shrink-0 border-b border-gray-100/50">
                <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Profile</h3>
                <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100/80 transition-colors">
                    <X className="w-4 h-4 text-gray-400" />
                </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-6">
                {/* Identity */}
                <div className="text-center pb-2">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-400 via-violet-500 to-purple-600 flex items-center justify-center text-white text-xl font-bold mx-auto mb-3 shadow-lg shadow-indigo-200/40">
                        {candidate.name.charAt(0).toUpperCase()}
                    </div>
                    <h4 className="text-base font-bold text-gray-900">{candidate.name}</h4>
                    {candidate.titles && candidate.titles[0] && (
                        <p className="text-sm text-gray-500 font-medium mt-0.5">{candidate.titles[0]}</p>
                    )}
                </div>

                {/* Score ring */}
                {qwen !== null && (
                    <div className="flex items-center justify-center gap-4">
                        <div className="flex flex-col items-center">
                            <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${qwenColor} flex items-center justify-center shadow-md`}>
                                <span className="text-lg font-bold text-white">{qwen.toFixed(0)}</span>
                            </div>
                            <span className="text-[10px] text-gray-400 mt-1.5 font-semibold uppercase tracking-wide">Match</span>
                        </div>
                        {vec !== null && (
                            <div className="flex flex-col items-center">
                                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center shadow-md">
                                    <span className="text-lg font-bold text-white">{(vec * 100).toFixed(0)}</span>
                                </div>
                                <span className="text-[10px] text-gray-400 mt-1.5 font-semibold uppercase tracking-wide">Vector</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Details */}
                <div className="space-y-5">
                    {typeof candidate.years_experience === 'number' && candidate.years_experience > 0 && (
                        <ProfileSection icon={Briefcase} title="Experience">
                            <p className="text-sm text-gray-700 font-medium">{candidate.years_experience} years</p>
                        </ProfileSection>
                    )}

                    {candidate.locations && candidate.locations.length > 0 && (
                        <ProfileSection icon={MapPin} title="Location">
                            <p className="text-sm text-gray-700 font-medium">{candidate.locations.join(' · ')}</p>
                        </ProfileSection>
                    )}

                    {candidate.titles && candidate.titles.length > 0 && (
                        <ProfileSection icon={User} title="Roles">
                            <div className="flex flex-wrap gap-1.5">
                                {candidate.titles.slice(0, 5).map((t) => (
                                    <span key={t} className="px-2.5 py-1 text-xs bg-indigo-50/80 text-indigo-600 rounded-lg font-medium">
                                        {t}
                                    </span>
                                ))}
                            </div>
                        </ProfileSection>
                    )}

                    {candidate.companies && candidate.companies.length > 0 && (
                        <ProfileSection icon={Building2} title="Companies">
                            <div className="flex flex-wrap gap-1.5">
                                {candidate.companies.slice(0, 5).map((c) => (
                                    <span key={c} className="px-2.5 py-1 text-xs bg-gray-50 text-gray-600 rounded-lg font-medium">
                                        {c}
                                    </span>
                                ))}
                            </div>
                        </ProfileSection>
                    )}

                    {candidate.skills && candidate.skills.length > 0 && (
                        <ProfileSection icon={GraduationCap} title="Skills">
                            <div className="flex flex-wrap gap-1.5">
                                {candidate.skills.slice(0, 12).map((s) => (
                                    <span key={s} className="px-2.5 py-1 text-xs bg-emerald-50/80 text-emerald-600 rounded-lg font-medium">
                                        {s}
                                    </span>
                                ))}
                            </div>
                        </ProfileSection>
                    )}

                    {(candidate.matched_skills?.length || candidate.matched_titles?.length || candidate.matched_certs?.length) && (
                        <ProfileSection icon={Star} title="Why they match">
                            <div className="space-y-2">
                                {candidate.matched_skills && candidate.matched_skills.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5">
                                        {candidate.matched_skills.map((s) => (
                                            <span key={s} className="px-2.5 py-1 text-xs bg-rose-50/80 text-rose-600 rounded-lg font-medium">
                                                {s}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {candidate.matched_titles && candidate.matched_titles.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5">
                                        {candidate.matched_titles.map((t) => (
                                            <span key={t} className="px-2.5 py-1 text-xs bg-indigo-50/80 text-indigo-600 rounded-lg font-medium">
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {candidate.matched_certs && candidate.matched_certs.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5">
                                        {candidate.matched_certs.map((c) => (
                                            <span key={c} className="px-2.5 py-1 text-xs bg-violet-50/80 text-violet-600 rounded-lg font-medium">
                                                {c}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </ProfileSection>
                    )}

                    {candidate.justification && (
                        <ProfileSection icon={Sparkles} title="David's take">
                            <p className="text-sm text-gray-600 italic leading-relaxed">"{candidate.justification}"</p>
                        </ProfileSection>
                    )}
                </div>
            </div>
        </div>
    );
};

const ProfileSection: React.FC<{
    icon: typeof Briefcase;
    title: string;
    children: React.ReactNode;
}> = ({ icon: Icon, title, children }) => (
    <div>
        <div className="flex items-center gap-1.5 mb-2.5">
            <Icon className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">{title}</span>
        </div>
        {children}
    </div>
);

export default CompanyDavidInbox;
